import React, { useState, useEffect, useContext, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { AuthContext } from "../AuthContext";
import { LoadingContext } from "../LoadingContext";
import Loading from "./Loading";
import "./AdminCustomerVisit.css";
import Swal from "sweetalert2";
import { toLocalDateString } from "../dateUtils";

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function AdminCustomerVisit() {
    const { authFetch } = useContext(AuthContext);
    const { setIsLoading } = useContext(LoadingContext);
    const navigate = useNavigate();
    const [loading, setLoading] = useState(true);

    const MySwal = Swal.mixin({
        customClass: {
            container: "my-swal-container",
        },
    });

    const [customDateRange, setCustomDateRange] = useState({
        startDate: new Date(),
        endDate: new Date(),
    });

    const [data, setData] = useState([]);

    const [total, setTotal] = useState(0);

    const handleRefresh = () => {
        fetchData();
    };

    const fetchData = async () => {
        try {

            const start = new Date(customDateRange.startDate);
            const end = new Date(customDateRange.endDate);

            var qs = "start=" + toLocalDateString(start) + "&end=" + toLocalDateString(end);

            const response = await authFetch(`${API_BASE_URL}/api/Report/customervisit?${qs}`);
            if (!response.ok) {
                throw new Error(`Error fetching : ${response.statusText}`);
            }
            const data = await response.json();
            setData(data);
            const total = data.reduce((sum, obj) => sum + obj.count, 0);
            setTotal(total);
            return data;
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "Failed to load data. Please try again." + JSON.stringify(error),
                icon: "error",
                confirmButtonColor: "#45a049",
            });
            return [];
        }
    };

    const handleCsvExport = () => {

        try {

            // Must match rowData below exactly - the leading No. column had no header,
            // which shifted every value one column to the right in Excel.
            var csvContent = "No.,Date,Total Visit\n";

            const escapeCSV = (value) => {
                if (value === null || value === undefined) return '';
                const str = String(value);
                if (str.includes(',') || str.includes('"') || str.includes('\n')) {
                    return `"${str.replace(/"/g, '""')}"`;
                }
                return str;
            };

            data.forEach((item, index) => {

                const rowData = [
                    index + 1,
                    escapeCSV(item.date),
                    escapeCSV(item.count),
                ];

                csvContent += rowData.join(',') + "\n";
            });

            // Leading empty cell keeps "Total" under the Date column and the figure
            // under Total Visit
            csvContent += ",Total," + total + "\n";

            const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8" });

            const url = URL.createObjectURL(blob);

            const link = document.createElement("a");
            link.href = url;
            link.download = `Ardency_CustomerVisit_Report_${toLocalDateString(new Date())}.csv`;

            document.body.appendChild(link);

            // Trigger the download
            link.click();

            // Clean up
            setTimeout(() => {
                document.body.removeChild(link);
                URL.revokeObjectURL(url);
            }, 100);
        } catch (error) {
            MySwal.fire({
                title: "Error!",
                text: "Failed to export CSV. Please try again.",
                icon: "error",
                confirmButtonColor: "#45a049",
            });
        }
    };

    const formatDate = (dateString) => {
        if (!dateString) return "N/A";
        try {
            const date = new Date(dateString);

            const day = String(date.getDate()).padStart(2, '0');
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const year = date.getFullYear();

            const hours = String(date.getHours()).padStart(2, '0');
            const minutes = String(date.getMinutes()).padStart(2, '0');
            const seconds = String(date.getSeconds()).padStart(2, '0');

            return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`; 
        } catch (e) {
            return dateString;
        }
    };

    // if (loading) {
    //     return (
    //         <div className="admin-sales-dashboard-container">
    //             <div className="admin-sales-dashboard-loading">
    //                 <Loading />
    //             </div>
    //         </div>
    //     );
    // }

    return (
        <div className="admin-sales-dashboard-container">
            {/* Header Section */}
            <div className="admin-sales-dashboard-header">
                <button
                    className="admin-sales-dashboard-back-btn"
                    onClick={() => navigate("/admin/dashboard")}
                >
                    <i className="fas fa-arrow-left"></i> Back to Dashboard
                </button>
            </div>

            <div className="admin-sales-dashboard-title">
                <h1>Customer Visit Analytics</h1>
            </div>

            <div className="admin-sales-dashboard-refresh" style={{
                marginBottom: '20px',
                display: 'flex',
                justifyContent: 'flex-end',
                gap: '10px'   
            }}>
                <button
                    className="admin-voucher-dashboard-print-btn"
                    onClick={handleCsvExport}
                >
                    <i className="fas fa-file-export"></i> Export Excel
                </button>
                <button
                    className="admin-sales-dashboard-refresh-btn"
                    onClick={handleRefresh}
                >
                    <i className="fas fa-sync-alt"></i> Search
                </button>
            </div>


            <div className="admin-sales-dashboard-row">
                <div className="admin-sales-dashboard-overview">
                    <div className="admin-sales-dashboard-print-section">

                        <div className="admin-sales-dashboard-controls">
                            <div className="admin-sales-dashboard-custom-date">
                                <div className="admin-sales-dashboard-control-group">
                                    <label>Start Date</label>
                                    <input
                                        type="date"
                                        value={customDateRange.startDate || ""}
                                        onChange={(e) =>
                                            setCustomDateRange({
                                                ...customDateRange,
                                                startDate: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                                <div className="admin-sales-dashboard-control-group">
                                    <label>End Date</label>
                                    <input
                                        type="date"
                                        value={customDateRange.endDate || ""}
                                        onChange={(e) =>
                                            setCustomDateRange({
                                                ...customDateRange,
                                                endDate: e.target.value,
                                            })
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="admin-sales-dashboard-row">

                <div className="admin-sales-dashboard-trends-container"  >
                    <div className="admin-sales-dashboard-print-section">
                        <div className="admin-sales-table-container">
                            {data.length > 0 ? (
                                <table className="admin-sales-table">
                                    <thead>
                                        <tr>
                                            <th>Date</th>
                                            <th>Total Visit</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.map((item, index) => (
                                            <tr key={index}>
                                                <td>{item.date}</td>
                                                <td>{item.count}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                    <tfoot>
                                        <tr>
                                            <td><strong>Total</strong></td>
                                            <td><strong>{total}</strong></td>
                                        </tr>
                                    </tfoot>
                                </table>
                            ) : (
                                <div className="admin-sales-empty">
                                    <i className="fas fa-shopping-cart"></i>
                                    <p className="primary-message">
                                        No Customer Visit for the selected period.
                                    </p>
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default AdminCustomerVisit;